/**
 * Step 5 - Aprovação explícita (importação de CLIENTES).
 *
 * Mostra contadores do dry-run, permite baixar CSV de erros e
 * exibe prévia paginada das linhas com problema (50/página).
 *
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import { useState, useMemo } from 'react';
import { AlertCircle, Download, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { Stat } from '@/components/crm/import/steps/Stat';
import type { ValidationCounters } from './types';
import { useCustomerImport } from '@/hooks/useCustomerImport';

interface Step5ApprovalCustomersProps {
  counters: ValidationCounters;
  jobId: string | null;
}

function statusVariant(status: string): 'destructive' | 'secondary' | 'outline' {
  if (status === 'invalid' || status === 'blacklisted') return 'destructive';
  if (status === 'duplicate') return 'secondary';
  return 'outline';
}

function statusLabel(status: string): string {
  switch (status) {
    case 'invalid': return 'Inválida';
    case 'duplicate': return 'Duplicada';
    case 'blacklisted': return 'Blacklist';
    default: return status;
  }
}

function extractReason(validationErrors: any, lastError: string | null): string {
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((e: any) => {
        if (typeof e === 'string') return e;
        const field = e?.field ? `[${e.field}] ` : '';
        return `${field}${e?.message ?? JSON.stringify(e)}`;
      })
      .join(' • ');
  }
  if (validationErrors && typeof validationErrors === 'object') return JSON.stringify(validationErrors);
  if (typeof validationErrors === 'string') return validationErrors;
  return lastError ?? '—';
}

export function Step5ApprovalCustomers({ counters, jobId }: Step5ApprovalCustomersProps) {
  const hasValid = counters.valid_count > 0;
  const rejectedCount =
    counters.invalid_count + counters.duplicate_count + counters.blacklisted_count;
  const hasRejected = rejectedCount > 0;

  const api = useCustomerImport();

  const [pageSize] = useState(50);
  const [pages, setPages] = useState(1);
  const limit = pageSize * pages;

  const [open, setOpen] = useState(hasRejected);
  const invalidQuery = api.useInvalidItems(jobId, { limit, offset: 0, enabled: hasRejected && open });

  const [isDownloading, setIsDownloading] = useState(false);

  const previewItems = invalidQuery.data?.items ?? [];
  const previewTotal = invalidQuery.data?.total ?? rejectedCount;
  const canLoadMore = previewItems.length < previewTotal;

  const tableRows = useMemo(() => previewItems, [previewItems]);

  async function downloadErrorsCsv() {
    if (!jobId || rejectedCount === 0) return;
    setIsDownloading(true);
    try {
      const items = await api.fetchAllJobItems(jobId, ['invalid', 'duplicate', 'blacklisted']);
      if (items.length === 0) {
        toast({ title: 'Nenhuma linha para exportar' });
        return;
      }

      const cols = new Set<string>();
      items.forEach((it) => Object.keys(it.raw_row ?? {}).forEach((k) => cols.add(k)));
      const headers = ['linha', 'status', 'motivo', ...Array.from(cols)];

      const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = items.map((it) => {
        const reason = extractReason(it.validation_errors, it.last_error);
        const base = [it.row_index, it.status, reason];
        const extras = Array.from(cols).map((c) => (it.raw_row as any)?.[c]);
        return [...base, ...extras].map(esc).join(';');
      });

      const csv = [headers.join(';'), ...rows].join('\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `importacao-clientes-erros-${jobId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: 'CSV exportado',
        description: `${items.length.toLocaleString('pt-BR')} linha(s) exportadas com motivo da rejeição.`,
      });
    } catch (err: any) {
      toast({
        title: 'Falha ao exportar CSV',
        description: err?.message ?? 'Erro inesperado.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-6 space-y-3">
        <h3 className="font-semibold text-lg">Resumo da validação</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Stat label="Total de linhas" value={counters.total_rows} />
          <Stat label="Válidas (serão importadas)" value={counters.valid_count} variant="success" />
          <Stat label="Duplicadas (puladas)" value={counters.duplicate_count} variant="warning" />
          <Stat label="Inválidas (não importadas)" value={counters.invalid_count} variant="destructive" />
          <Stat label="Blacklist LGPD" value={counters.blacklisted_count} variant="destructive" />
        </div>

        {hasRejected && jobId && (
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadErrorsCsv}
              disabled={isDownloading || rejectedCount === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloading
                ? 'Gerando CSV…'
                : `Baixar CSV de erros (${rejectedCount.toLocaleString('pt-BR')})`}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Exporta linhas não importadas com motivo, para correção offline e nova tentativa.
            </p>
          </div>
        )}
      </div>

      {hasRejected && jobId && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="rounded-lg border">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <span className="font-medium text-sm">
                    Linhas com problema ({rejectedCount.toLocaleString('pt-BR')})
                  </span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="border-t">
                {invalidQuery.isLoading && previewItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Carregando…</div>
                ) : invalidQuery.isError ? (
                  <div className="p-6 text-center text-sm text-destructive">
                    Erro ao carregar prévia: {(invalidQuery.error as Error)?.message}
                  </div>
                ) : previewItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Nenhuma linha problemática encontrada.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="w-20"># Linha</TableHead>
                            <TableHead className="w-28">Status</TableHead>
                            <TableHead>Motivo</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableRows.map((it) => (
                            <TableRow key={it.id}>
                              <TableCell className="font-mono text-xs">{it.row_index}</TableCell>
                              <TableCell>
                                <Badge variant={statusVariant(it.status)}>{statusLabel(it.status)}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {extractReason(it.validation_errors, it.last_error)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20">
                      <span className="text-xs text-muted-foreground">
                        Exibindo {previewItems.length.toLocaleString('pt-BR')} de{' '}
                        {previewTotal.toLocaleString('pt-BR')}
                      </span>
                      {canLoadMore && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPages((p) => p + 1)}
                          disabled={invalidQuery.isFetching}
                        >
                          {invalidQuery.isFetching ? 'Carregando…' : `Carregar mais ${pageSize}`}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {!hasValid ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive bg-destructive/5 p-4">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm">Nenhuma linha válida encontrada. Revise o CSV e tente novamente.</p>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          <span className="text-foreground font-medium">
            {counters.valid_count.toLocaleString('pt-BR')} clientes
          </span>{' '}
          serão importados em segundo plano. Acompanhe o progresso na próxima tela.
        </div>
      )}
    </div>
  );
}
